import { kebabCase } from 'lodash';
import {
  AttributeOptionValueType,
  AttributesTypesWithCustomValueMappings,
  GroupAttribute,
  GroupLevelAttribute,
  GroupLevelAttributeListType,
  GroupLevelAttributeOption,
  GroupLevelAttributeValue,
  SimplifiedGroupLevelAttributePrimitive,
  SimplifiedGroupLevelAttributeValues,
} from '../types/jaas-group-level-attributes.types';
import { Timezone } from './../dto/jtms-public-course.dtos';

/* Basic clone function
 * TODO: Add proper error throwing. */
export function clone<Type>(argv: Type): Type {
  if (argv && typeof argv !== 'function') {
    return JSON.parse(JSON.stringify(argv)) as Type;
  }
  return argv as Type;
}

/* Default group level attribute related functions.
 *
 * NOTE:
 * As of now, there are only two default group attributes:
 * title and logo. (applies to both group LEVEL attributes and group TEMPLATE attributes.)
 * Title and logo are currently the first attributes for each group.
 *
 * TODO: Consider creating table to store default attributes for better standardization. */
export function isTitleAttribute(attribute: GroupLevelAttribute): boolean {
  if (
    attribute.groupLevelAttributeType.type === 'text' &&
    attribute.label.trim().toLowerCase() === 'title'
  ) {
    return true;
  }
  return false;
}

export function isLogoAttribute(attribute: GroupLevelAttribute): boolean {
  if (
    attribute.groupLevelAttributeType.type === 'images' &&
    attribute.label.trim().toLowerCase() === 'logo'
  ) {
    return true;
  }
  return false;
}

// TOOD: Remove after refactor
export function isLogo(attribute: GroupAttribute): boolean {
  if (
    attribute.groupLevelAttributeType.type === 'images' &&
    attribute.label.trim().toLowerCase() === 'logo'
  ) {
    return true;
  }
  return false;
}

// TOOD: Remove after refactor
export function isTitle(attribute: GroupAttribute): boolean {
  if (
    attribute.groupLevelAttributeType.type === 'text' &&
    attribute.label.trim().toLowerCase() === 'title'
  ) {
    return true;
  }
  return false;
}

// Centralized object for storing default course GLAs
export const DEFAULT_COURSE_ATTRIBUTES: Record<string, DefaultAttributeProperties> = {
  TITLE: { type: 'text', label: 'title' },
  DESCRIPTION: { type: 'text area', label: 'description' },
  START_DATE: { type: 'datetime', label: 'start date' },
  END_DATE: { type: 'datetime', label: 'end date' },
  TIMEZONE: { type: 'timezone', label: 'timezone' },
  PRIMARY_ORGANISATION: { type: 'organisation', label: 'primary organisation' },
  TRAINER: { type: 'trainer', label: 'trainers' },
  LOCATION: { type: 'location', label: 'location' },
  CONTACT: { type: 'contact', label: 'contacts' },
  REGISTRATION_LINK: { type: 'link', label: 'registration link' },
  COURSE_SIZE: { type: 'integer', label: 'course size' },
  COURSE_TYPE: { type: 'list', label: 'course type' },
  STATUS: { type: 'list', label: 'status' },
};

export const DEFAULT_GROUP_ATTRIBUTES: Record<string, DefaultAttributeProperties> = {
  TITLE: { type: 'text', label: 'title' },
  LOGO: { type: 'images', label: 'logo' },
};

// Generic checker function for attribute type AND label
function isAttributeOfMatchingTypeAndLabel(
  attribute: GroupAttribute | GroupLevelAttribute,
  defaultProperties: DefaultAttributeProperties,
): boolean {
  return (
    attribute.groupLevelAttributeType.type === defaultProperties.type &&
    attribute.label.trim().toLowerCase() === defaultProperties.label.toLowerCase()
  );
}

// Generic checker function for attribute type
export function isAttributeOfType(
  attribute: GroupAttribute | GroupLevelAttribute,
  type: string,
): boolean {
  return (
    attribute.groupLevelAttributeType.type === type
  );
}

interface DefaultAttributeProperties {
  type: string;
  label: string;
}

export function extractGroupLevelAttribute(
  groupLevelAttributeValue: GroupLevelAttributeValue[]
): GroupLevelAttribute[] {
  const groupLevelAttribute: GroupLevelAttribute[] = groupLevelAttributeValue.map(
    groupLevelAttributeValue => {
      return {
        id: +groupLevelAttributeValue.id!,
        label: groupLevelAttributeValue.label,
        option: groupLevelAttributeValue.option,
        groupLevelAttributeType: groupLevelAttributeValue.groupLevelAttributeType,
        attributeStatus: groupLevelAttributeValue.attributeStatus,
      }
    }
  )

  return groupLevelAttribute;
}

/**
 * Transforms an array of GroupLevelAttributeValue objects into a single,
 * simplified key-value object, intelligently processing the 'value' based on its type.
 *
 * - For simple types (`string`, `string[]`, `null`), the value is passed through.
 * - For `GroupLevelAttributeListType`, only the `selected` property is extracted.
 * - For `GroupLevelAttributeOption[]`, the items in the array are transformed into a
 *   simplified object of key-value objects (placeholder as key).
 *
 * @param  groupLevelAttributeValues - The array of attribute values to transform.
 * @param keyPrefix - An optional prefix to prepend to each generated key.
 * @returns  A key-value object representing the simplified attributes.
 */
export function generateSimplifiedGroupLevelAttributeValues(
  groupLevelAttributeValues: GroupLevelAttributeValue[],
  keyPrefix?: string,
  timezone?: Timezone
): SimplifiedGroupLevelAttributeValues {
  if (!Array.isArray(groupLevelAttributeValues) || groupLevelAttributeValues.length === 0) {
    return {};
  }

  // Use reduce to iterate over the array and build a single object.
  return groupLevelAttributeValues.reduce((accumulator, currentAttribute) => {
    const { label, value } = currentAttribute;

    // Default to the original value
    let simplifiedValue: SimplifiedGroupLevelAttributePrimitive = value;

    // 1.  Extract group level attribute from GLA value
    const attribute = extractGroupLevelAttribute([currentAttribute])[0];

    // 2. Specific handling based on attribute types
    // 2a. Generic handling for all list types. For a list type, we only care about
    //     what was selected as value
    if (
      isAttributeOfType(
        attribute,
        AttributesTypesWithCustomValueMappings.LISTS
      )
    ) {
      simplifiedValue = (value as GroupLevelAttributeListType).selected;
    }

    // 2b. Admin fee, coordinate, and address types should be represented with the
    //     placeholder as key, and attribute value as value.
    if (
      value &&
      Array.isArray(value) &&
      (
        isAttributeOfType(
          attribute,
          AttributesTypesWithCustomValueMappings.COORDINATES
        ) ||
        isAttributeOfType(
          attribute,
          AttributesTypesWithCustomValueMappings.ADMIN_FEE
        ) ||
        isAttributeOfType(
          attribute,
          AttributesTypesWithCustomValueMappings.ADDRESS
        )
      )
    ) {
      simplifiedValue = transformGroupLevelAttributeOptionsArrayToObject(value as GroupLevelAttributeOption[]);
    }

    // 3. For values that do not require specific handling, generate the slugified key
    //    from the label.
    // NOTE: Using lodash's `kebabCase()` to prevent importing libraries into jbi-shared
    // (slugify is an external library).
    const slugifiedLabel = kebabCase(label);

    // 4. Construct the final key with the optional prefix.
    // e.g., 'course-admin-fee'
    const finalKey = keyPrefix ? `${keyPrefix}-${slugifiedLabel}` : slugifiedLabel;

    // 5. Assign the value to the new key on the accumulator object
    accumulator[finalKey] = simplifiedValue;

    // 6. Return the accumulator for the next iteration.
    return accumulator;
  }, {} as SimplifiedGroupLevelAttributeValues); // Start with an empty object
}

/**
 * Transforms an array of GroupLevelAttributeOption into a simplified key-value object.
 * The keys are generated by kebab-casing the 'placeholder' of each option.
 *
 * @param options - The array of attribute options to transform.
 * @returns A simplified object with dynamic key-value pairs.
 */
function transformGroupLevelAttributeOptionsArrayToObject(
  options: GroupLevelAttributeOption[]
): { [key: string]: AttributeOptionValueType } {
  if (!Array.isArray(options)) {
    return {};
  }

  return options.reduce((accumulator, option) => {
    if (!option.placeholder) {
      throw new Error('Attribute options has missing or malformed placeholder.');
    }

    // Only assign key-value pair if status is 'ACTIVE' and has value
    if (option.status === 'ACTIVE' && option.value) {
      // Generate a key from the placeholder (e.g., "Street Address" -> "street-address")
      // Using lodash's `kebabCase()` to prevent importing libraries into jbi-shared (slugify is an external library).
      const key = kebabCase(option.placeholder);
      accumulator[key] = option.value;
    }

    return accumulator;
  }, {} as { [key: string]: AttributeOptionValueType });
}

export function isDefaultCourseGLA(attribute: GroupLevelAttribute): boolean {
  return Object.values(DEFAULT_COURSE_ATTRIBUTES).some(config =>
    isAttributeOfMatchingTypeAndLabel(attribute, config),
  );
}

export function isDefaultGroupAttribute(attribute: GroupLevelAttribute): boolean {
  return Object.values(DEFAULT_GROUP_ATTRIBUTES).some(config =>
    isAttributeOfMatchingTypeAndLabel(attribute, config),
  );
}

// TOOD: Remove after refactor
export function isDefault(attribute: GroupAttribute): boolean {
  return isTitle(attribute) || isLogo(attribute);
}

/**
 * Simple debouncer for synchronous functions
 *
 * Done thru restarting a local setTimeout timer everytime the function is triggered.
 */
let debounceTimer: undefined | ReturnType<typeof setTimeout>;

export function debouncer(
  millisecond: number,
  callbackFn: (cbArgv?: any) => any,
  ...params: any
): any {
  clearTimeout(debounceTimer as ReturnType<typeof setTimeout>);
  debounceTimer = setTimeout(() => {
    if (params) {
      return callbackFn(...params);
    }
    return callbackFn();
  }, millisecond);
}

/**
 * This debouncer is focused on use-cases where multiple debouncers are required concurrently.
 * For example, a form may contain multiple fields and each field may require a debouncer.
 */
const componentLevelDebouncerTimers: {
  [key: string]: ReturnType<typeof setTimeout>;
} = {};

export function componentLevelDebouncer(
  millisecond: number,
  key: string | number,
  callbackFn: (cbArgv?: any) => any,
  ...params: any
): any {
  clearTimeout(
    componentLevelDebouncerTimers['' + key] as ReturnType<typeof setTimeout>,
  );
  componentLevelDebouncerTimers['' + key] = setTimeout(() => {
    if (params) {
      return callbackFn(...params);
    }
    return callbackFn();
  }, millisecond);
}

/**
 * Returns the content that is wrapped between html tags.
 */
export function getHtmlContent(strigifiedHtml: string): string {
  const htmlContentExp: RegExp = /<\/?[^>]+(>|$)/g;
  return strigifiedHtml.replace(htmlContentExp, ' ').trim();
}

/**
 * Formats a date or date string into a localized, human-readable string.
 * It gracefully handles potential parsing errors by returning the original input.
 * 
 * @example
 * // Using default format options: 
 * Thu, 1 January 2026, 10:00 am GMT+8
 * 
 * @param dateTime The date to format, provided as either a `Date` object or a string that can be parsed by the `Date` constructor.
 * @param locale The locale identifier for formatting conventions, e.g., 'en-US'. Defaults to 'en-AU'.
 * @param timezone The IANA timezone name to which the date should be converted, e.g., 'UTC'. Defaults to 'Australia/Adelaide'.
 * @param options An `Intl.DateTimeFormatOptions` object to customize the output. A comprehensive default is provided.
 * @returns The formatted date string, or the original `dateTime` converted to a string if formatting fails.
 */
export function formatDateTime(
  dateTime: string | Date,
  locale: string = 'en-AU',
  timezone: string = 'Australia/Adelaide',
  options: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    weekday: 'short',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
    hour12: true
  }
): string {
  try {
    const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
    return date.toLocaleDateString(locale, options);
  } catch (error) {
    return String(dateTime);
  }
}